import { API } from '../config/env';
import { apiClient } from './client';
import { useAuthStore } from '../store/authStore';
import { useCompanyStore } from '../store/companyStore';

export interface AuthTokenResponse {
  accessToken: string;
  refreshToken?: string | null;
  grantType?: string;
  sessionExpiresAt?: number | null;
  rememberMe?: boolean | null;
}

export interface RefreshTokenResponse {
  accessToken: string;
  sessionExpiresAt?: number | null;
  rememberMe?: boolean | null;
}

export function startKakaoLogin(rememberMe = false) {
  const url = new URL(API.kakaoAuthorize);
  url.searchParams.set('remember', rememberMe ? 'true' : 'false');
  window.location.href = url.toString();
}

export async function signIn(
  username: string,
  password: string,
  rememberMe: boolean
): Promise<AuthTokenResponse> {
  const { data } = await apiClient.post<AuthTokenResponse>('/sign-in', {
    username,
    password,
    rememberMe,
  });
  return data;
}

export async function signUp(payload: {
  username: string;
  password: string;
  email: string;
  nickname: string;
  phoneNumber: string;
  phoneVerificationToken: string;
  birthDate: string;
  gender?: string;
}): Promise<void> {
  await apiClient.post('/sign-up', payload);
}

export async function sendPhoneCode(phoneNumber: string): Promise<void> {
  await apiClient.post('/auth/phone/send', { phoneNumber });
}

export async function verifyPhoneCode(
  phoneNumber: string,
  code: string
): Promise<{ verificationToken: string }> {
  const { data } = await apiClient.post<{ verificationToken: string }>('/auth/phone/verify', {
    phoneNumber,
    code,
  });
  return data;
}

export async function refreshAccessToken(silent = false): Promise<RefreshTokenResponse> {
  const { data } = await apiClient.post<RefreshTokenResponse>('/refresh', undefined, {
    silentAuth: silent,
  });
  return data;
}

export async function logout() {
  const socialSub = useAuthStore.getState().socialSub;
  // 카카오 providerId(숫자)인 경우만 카카오 연동 로그아웃
  const isKakaoUser = !!socialSub && /^\d+$/.test(socialSub);
  if (isKakaoUser) {
    try {
      await apiClient.get('/oauth/kakao/logout');
    } catch {
      // ignore — 아래 sign-out에서 앱 세션은 정리
    }
  }
  try {
    await apiClient.post('/sign-out');
  } catch {
    // ignore
  } finally {
    useAuthStore.getState().clearAuth();
    useCompanyStore.getState().clearCompany();
  }
}

export async function bootstrapAuth(): Promise<void> {
  const store = useAuthStore.getState();
  if (store.accessToken) {
    store.setHydrated(true);
    return;
  }
  try {
    const refreshed = await refreshAccessToken(true);
    store.setTokens({
      accessToken: refreshed.accessToken,
      socialSub: store.socialSub,
      rememberMe: refreshed.rememberMe ?? store.rememberMe,
      sessionExpiresAt: refreshed.sessionExpiresAt ?? null,
    });
  } catch {
    store.clearAuth();
  } finally {
    useAuthStore.getState().setHydrated(true);
  }
}

export type AccessLogItem = {
  accessLogId?: number;
  loginMethod?: string | null;
  ipAddress?: string | null;
  deviceType?: string | null;
  browser?: string | null;
  os?: string | null;
  accessedAt?: string | null;
};

export type AccessLogPage = {
  content: AccessLogItem[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
};

export async function fetchMyProfile(): Promise<{
  nickname?: string | null;
  loginId?: string | null;
  profileImg?: string | null;
  lastLoginAt?: string | null;
  recentAccessLogs?: AccessLogItem[];
}> {
  const { data } = await apiClient.get<{
    nickname?: string | null;
    loginId?: string | null;
    profileImg?: string | null;
    lastLoginAt?: string | null;
    recentAccessLogs?: AccessLogItem[];
  }>('/member/profile');
  return data;
}

export async function fetchAccessLogs(page = 0, size = 10): Promise<AccessLogPage> {
  const { data } = await apiClient.get<AccessLogPage>('/member/access-logs', {
    params: { page, size },
  });
  return data;
}

export async function updateMyProfile(payload: {
  nickname: string;
  profileImg?: File | null;
}): Promise<void> {
  const form = new FormData();
  form.append('nickname', payload.nickname);
  if (payload.profileImg) {
    form.append('profileImg', payload.profileImg);
  }
  await apiClient.put('/member/profile', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

export function formatSessionRemaining(ms: number): string {
  if (ms <= 0) return '0분';
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  if (days > 0) return `${days}일 ${hours}시간`;
  if (hours > 0) return `${hours}시간 ${minutes}분`;
  if (minutes > 0) return `${minutes}분 ${String(seconds).padStart(2, '0')}초`;
  return `${seconds}초`;
}
